import { getCreatePlacementTypes } from '@frontend/store/creative-studio/index';
import {
  searchAndShowProductImages,
  resolveAttachmentReferences,
  generateAndShowBackgroundPreviews,
  showAttachmentBackground,
  stripExampleReferences,
} from '@frontend/store/creative-studio/chat-function-handlers/search-products';

/**
 * Handler for the create_campaign AI function.
 *
 * Matches the AI-returned network name against allNetworks in the Vuex store.
 * - If the network has multiple placement types: shows an action-button widget
 *   so the user can pick a placement type (or "All types").
 * - If the network has a single placement type: auto-selects all sizes for that
 *   type so they appear in the dock bar.
 */
export const handleCreateCampaign = async ($message, { state, commit, dispatch }) => {
  let networkName = '';
  let requestedPlacementType = '';
  let productReferences = [];
  let args = {};
  let msgAtts = [];
  let backgroundImageUrl = null;
  let argProductRefs = [];
  let bgAttExclude = [];

  try {
    args = typeof $message.function_call.arguments === 'string'
      ? JSON.parse($message.function_call.arguments)
      : $message.function_call.arguments || {};

    networkName = (args.network_name || '').trim();
    requestedPlacementType = (args.placement_type || '').trim();

    const lastUserMsg = [...(state?.messages || [])].reverse().find((m) => m.role === 'user' && Array.isArray(m.attachments));
    msgAtts = lastUserMsg?.attachments || [];

    // Separate background ref (is_background: true) from product refs before any resolution.
    const bgRef = (args.product_references || []).find((r) => r.is_background);
    argProductRefs = (args.product_references || []).filter((r) => !r.is_background);
    // Drop refs the model pulled out of "e.g." example text (POR-13481).
    argProductRefs = stripExampleReferences(argProductRefs, args.image_prompt);

    if (bgRef?.type === 'attachment') {
      const bgIdx = parseInt(bgRef.value, 10);
      const bgAtt = !Number.isNaN(bgIdx) ? msgAtts[bgIdx] : null;
      backgroundImageUrl = bgAtt?._resolvedUrl || bgAtt?.payload || bgAtt?.url || bgAtt?.preview || null;
      bgAttExclude = [bgIdx];
    }

    productReferences = resolveAttachmentReferences(
      argProductRefs,
      $message.savedAttachments,
      state?.messages || [],
      false,
      bgAttExclude
    );

    // Upgrade refs that the AI resolved from an attachment index but whose source
    // attachment is a product_asset — change type from 'url' to 'product_asset' in place.
    productReferences = productReferences.map((ref, i) => {
      const origRef = argProductRefs[i];

      if (origRef?.type !== 'attachment') {
        return ref;
      }

      const attIndex = parseInt(origRef.value, 10);
      const srcAtt = msgAtts[attIndex];

      if (srcAtt?.asset_type !== 'product_asset') {
        return ref;
      }

      return {
        ...ref,
        type: 'product_asset',
        value: srcAtt?.original_path || ref.value,
        name: srcAtt.name || ref.display_name || 'Product Image',
        display_name: srcAtt?.original_name || ref.display_name || 'Product Image',
        original_name: srcAtt?.original_name || 'Product Image',
        _attachmentName: srcAtt.name,
        _fromUserAttachment: true,
      };
    });

    // Add any product_asset attachments the AI did not reference at all.
    const coveredNames = new Set(productReferences.map((r) => r._attachmentName).filter(Boolean));

    msgAtts
      .filter((att) => att?.asset_type === 'product_asset' && !coveredNames.has(att.name))
      .forEach((att) => {
        const url = att._resolvedUrl || att.payload || att.url || att.preview;

        if (!url) {
          return;
        }

        productReferences.push({
          type: 'product_asset',
          value: att?.original_path ?? url,
          name: att.name || 'Product Image',
          display_name: att?.original_name || att.name || 'Product Image',
          original_name: att?.original_name || 'Product Image',
          _attachmentName: att.name,
          _fromUserAttachment: true,
        });
      });

    // Add plain-URL attachments (non-product_asset) that the AI did not reference.
    const coveredAttachmentIndices = new Set(
      argProductRefs
        .filter((r) => r.type === 'attachment')
        .map((r) => parseInt(r.value, 10))
    );

    msgAtts.forEach((att, i) => {
      if (att?.asset_type === 'product_asset' || coveredAttachmentIndices.has(i) || bgAttExclude.includes(i)) {
        return;
      }

      const url = att._resolvedUrl || att.payload || att.url || att.preview;

      if (!url) {
        return;
      }

      productReferences.push({
        type: 'url',
        value: url,
        display_name: att?.original_name || att.name || `attachment ${i}`,
        _fromUserAttachment: true,
        _attachmentName: `__plain_${i}`,
      });
    });

    if (args.image_prompt) {
      commit('SET_IMAGE_PROMPT', args.image_prompt);
    }

    if (args.background_scene || productReferences.length || args.text_elements || backgroundImageUrl) {
      commit('SET_PARSED_PROMPT', {
        ...(state.parsedPrompt || {}),
        background_scene: args.background_scene || null,
        product_references: productReferences.length > 0
          ? productReferences
          : (state.parsedPrompt?.product_references || []),
        text_elements: args.text_elements || [],
        _backgroundImageUrl: backgroundImageUrl || null,
      });
      await dispatch('saveConversationState');
    }
  } catch (e) {
    return;
  }

  if (!networkName) {
    return;
  }

  // Find matching network (case-insensitive partial match)
  const lowerName = networkName.toLowerCase();
  const network = state.allNetworks.find(
    (n) => n.name.toLowerCase() === lowerName
  ) || state.allNetworks.find(
    (n) => n.name.toLowerCase().includes(lowerName) || lowerName.includes(n.name.toLowerCase())
  );

  if (!network) {
    const available = state.allNetworks.map((n) => n.name).filter(Boolean);
    const availableLine = available.length
      ? ` In the meantime you can use one of your available networks: <b>${available.join('</b>, <b>')}</b>.`
      : '';
    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: `The <b>${networkName}</b> network isn't set up for your company yet, so I can't create this campaign. Please ask your admin to add it.${availableLine}`,
    });
    return;
  }

  // Search for product images before showing size selection
  if (productReferences.length > 0) {
    await searchAndShowProductImages(productReferences, { commit, dispatch });

    // Re-resolve now that _foundProducts have been committed to state.messages.
    // ADD_MESSAGE is synchronous so state.messages is already updated.
    const reresolvedRefs = resolveAttachmentReferences(
      argProductRefs,
      $message.savedAttachments,
      state.messages,
      false,
      bgAttExclude
    );

    if (reresolvedRefs.some((r) => r.type === 'url')) {
      // Preserve UUIDs assigned by searchAndShowProductImages on the previous parsed_prompt.
      // Without this, the second SET_PARSED_PROMPT below would replace refs with new
      // objects lacking the id, breaking the keep_background / toggle_product_selection flow.
      const prevRefs = state.parsedPrompt?.product_references || [];
      const findPrevId = (ref) => {
        const prev = prevRefs.find((p) => p.id && (
          p.value === ref.value
          || (ref._attachmentName && p._attachmentName === ref._attachmentName)
        ));
        return prev?.id;
      };

      const upgradedRefs = reresolvedRefs.map((ref, i) => {
        const origRef = argProductRefs[i];
        const preservedId = findPrevId(ref);
        if (origRef?.type !== 'attachment') {
          return preservedId ? { ...ref, id: preservedId } : ref;
        }
        const srcAtt = msgAtts[parseInt(origRef.value, 10)];

        if (srcAtt?.asset_type !== 'product_asset') {
          return preservedId ? { ...ref, id: preservedId } : ref;
        }
        return {
          ...ref,
          ...(preservedId ? { id: preservedId } : {}),
          type: 'product_asset',
          value: srcAtt?.original_path || srcAtt.payload || srcAtt.url || srcAtt.preview,
          name: srcAtt.name || ref.display_name || 'Product Image',
          original_name: srcAtt?.original_name || ref.display_name || 'Product Image',
          _attachmentName: srcAtt.name,
          _fromUserAttachment: true,
        };
      });

      const coveredAttNames = new Set(upgradedRefs.map((r) => r._attachmentName).filter(Boolean));

      const extraRefs = productReferences.filter(
        (r) => r._fromUserAttachment && !coveredAttNames.has(r._attachmentName)
      );

      commit('SET_PARSED_PROMPT', {
        ...(state.parsedPrompt || {}),
        product_references: [...upgradedRefs, ...extraRefs],
      });

      await dispatch('saveConversationState');
    }
  }

  if (state.isDescribingPlacement) {
    commit('SET_DESCRIBING_PLACEMENT', false);

    const parsedPrompt = state.parsedPrompt || {};
    const backgroundScene = parsedPrompt.background_scene;

    if ((backgroundScene || backgroundImageUrl) && !parsedPrompt._backgroundTriggered) {
      commit('SET_PARSED_PROMPT', { ...parsedPrompt, _backgroundTriggered: true });

      await dispatch('saveConversationState');

      if (backgroundImageUrl) {
        await showAttachmentBackground(backgroundImageUrl, { dispatch });
      } else {
        generateAndShowBackgroundPreviews(backgroundScene, { commit, dispatch }, { width: 1920, height: 1080 });
      }
    }

    return;
  }

  const placementTypes = getCreatePlacementTypes(network);

  // If the user specified a placement type, auto-select that one directly
  if (requestedPlacementType) {
    const lowerRequested = requestedPlacementType.toLowerCase();
    const matchedPt = placementTypes.find(
      (p) => p.label.toLowerCase() === lowerRequested || p.label.toLowerCase().includes(lowerRequested)
    );

    if (matchedPt) {
      const sizes = matchedPt.sizes || [];
      sizes.forEach((size) => {
        commit('TOGGLE_SIZE', { sizeId: size.id, selected: true });
      });

      await dispatch('addAndSaveMessage', {
        role: 'assistant',
        content: `Selected ${sizes.length} sizes for <b>${network.name}</b> (${matchedPt.label}). You can update the selections below.`,
      });
      return;
    }
  }

  if (placementTypes.length > 1) {
    // Multiple placement types and none specified — show picker widget
    const buttons = placementTypes.map((pt) => ({
      label: pt.label,
      action: `select_pt:${pt.label}`,
      variant: 'primary',
    }));

    buttons.push({
      label: 'All types',
      action: 'select_pt:all',
      variant: 'primary',
    });

    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: `<b>${network.name}</b> has ${placementTypes.length} placement types. Which would you like?`,
      _networkPublicId: network.public_id,
      widgets: [
        {
          type: 'action_buttons',
          id: 'placement_type_select',
          buttons,
        },
      ],
    });

  } else if (placementTypes.length === 1) {
    // Single placement type — auto-select all sizes
    const sizes = placementTypes[0].sizes || [];

    sizes.forEach((size) => {
      commit('TOGGLE_SIZE', { sizeId: size.id, selected: true });
    });

    const messagePayload = {
      role: 'assistant',
      content: `Selected ${sizes.length} sizes for <b>${network.name}</b> (${placementTypes[0].label}). You can update the selections below.`,
    };

    if (state.pendingPlacementDescribe) {
      messagePayload.widgets = [{
        type: 'action_buttons',
        id: 'placement_describe_buttons',
        buttons: [
          {
            label: 'Describe the placements',
            action: 'placement_describe',
            variant: 'light',
          },
          {
            label: 'Empty templates',
            action: 'placement_empty_templates',
            variant: 'light',
          },
        ],
      }];
      commit('SET_PENDING_PLACEMENT_DESCRIBE', false);
    }

    await dispatch('addAndSaveMessage', messagePayload);

    const parsedPrompt = state.parsedPrompt || {};

    const backgroundScene = parsedPrompt.background_scene;

    if ((backgroundScene || backgroundImageUrl) && !parsedPrompt._backgroundTriggered) {
      commit('SET_PARSED_PROMPT', { ...parsedPrompt, _backgroundTriggered: true });

      await dispatch('saveConversationState');

      if (backgroundImageUrl) {
        await showAttachmentBackground(backgroundImageUrl, { dispatch });
      } else {
        generateAndShowBackgroundPreviews(backgroundScene, { commit, dispatch }, { width: 1920, height: 1080 });
      }
    }
  }
};
